import { atkinsonMask } from '../family-a/atkinson';
import { floydSteinbergMask } from '../family-a/floyd-steinberg';
import { buildStampThreshold } from '../family-a/stamp';
import { wernessMask } from '../family-a/werness';
import type { FamilyCMethod, Polarity } from '../types';
import { BLUE_NOISE_128, BLUE_NOISE_SIZE } from '../util/blue-noise-data';
import { sampleBlueNoiseBilinear } from '../util/blue-noise-sample';
import { downscaleBoxAverage } from '../util/downscale';
import { rotateImageNN, WHITE_RGBA } from '../util/rotate-nn';
import { BAYER_MATRICES } from '../util/threshold-maps';

/**
 * Family C - pixel-to-dot transformation pipeline.
 *
 * Five stages, output sized to the original source. The defining
 * choice is that the SOURCE itself is geometrically modified
 * before the algorithm runs:
 *
 *   1. Rotate source by -gridAngleDeg (NN, white pad).
 *   2. Downsample rotated source by cellSize → one pixel per dot.
 *   3. Run the chosen algorithm on the small cell buffer →
 *      1-bit cell mask (one decision per dot).
 *   4. Render the mask at the rotated-source resolution. Each
 *      "on" cell becomes the stamp silhouette at coverage; each
 *      "off" cell stays background.
 *   5. Rotate render back by +gridAngleDeg, crop to original
 *      source dims.
 *   6. Per-pixel blackTh / whiteTh clamp against the ORIGINAL
 *      (un-rotated, full-res) source. Solid black source stays
 *      solid black at any cellSize / angle; line art keeps its
 *      edges.
 *   7. Optional boundary noise displaces stamp sampling before the
 *      binary paint/skip decision. The output remains strict black
 *      and white; only the dot edge gets an analog wobble.
 *
 * The "1 pixel = 1 dot" model means cellSize controls dot
 * pitch by literally shrinking the source - bigger dots ≡
 * smaller working buffer, not a wider stamp threshold tile.
 *
 * Multi-region note: when called from the bbox-cropped multi-
 * region path, the rotation pivots around the BBOX centre, not
 * the source centre. That keeps each region's pipeline self-
 * contained but means dots in different regions will not share
 * a single image-centre rotation pivot. Acceptable for v0.2.1.
 */

interface RegionOrigin {
  originX: number;
  originY: number;
  fullW: number;
  fullH: number;
  labelMap?: ImageData;
  label?: number;
}

export interface FamilyCCellMaskSnapshot {
  mask: Uint8Array;
  rotatedWidth: number;
  rotatedHeight: number;
  cellWidth: number;
  cellHeight: number;
  cellSize: number;
}

export function buildFamilyCCellMaskSnapshot(
  source: ImageData,
  method: FamilyCMethod,
): FamilyCCellMaskSnapshot {
  const cellSize = Math.max(1, Math.floor(method.cellSize));
  const angle = method.gridAngleDeg ?? 0;
  const rotated = rotateImageNN(source, -angle, WHITE_RGBA);
  const cellGray = downscaleBoxAverage(rotated, cellSize);
  return {
    mask: computeCellMask(cellGray, method),
    rotatedWidth: rotated.width,
    rotatedHeight: rotated.height,
    cellWidth: cellGray.width,
    cellHeight: cellGray.height,
    cellSize,
  };
}

export function runFamilyCTransform(
  source: ImageData,
  method: FamilyCMethod,
  _origin?: RegionOrigin,
): ImageData {
  const targetW = source.width;
  const targetH = source.height;
  const angle = method.gridAngleDeg ?? 0;
  const blackTh = method.blackThreshold ?? 0.02;
  const whiteTh = method.whiteThreshold ?? 0.98;

  const cellMask = buildFamilyCCellMaskSnapshot(source, method);

  // Stage 4: render the mask at the rotated-render resolution.
  // Each on-cell becomes the stamp silhouette at the configured
  // coverage; each off-cell stays background.
  const rotatedRender = renderCellMaskWithStamp(
    cellMask.mask,
    cellMask.cellWidth,
    cellMask.cellHeight,
    cellMask.cellSize,
    method.stamp,
    method.shapeAngleDeg ?? 0,
    method.shapeFlipX ?? false,
    method.shapeFlipY ?? false,
    method.polarity,
    method.grainStrength ?? 0,
    method.grainScale ?? 1,
  );

  // Stage 5: rotate back by +angle, mapping into the original
  // source dimensions in one pass via the outDims override.
  const unrotated = rotateImageNN(rotatedRender, angle, WHITE_RGBA, {
    w: targetW,
    h: targetH,
  });

  // Stage 6: blackTh / whiteTh clamp from the original full-res
  // source. Preserves line art and solid blocks regardless of
  // cellSize or angle.
  applySolidInputClamp(unrotated, source, method.polarity, blackTh, whiteTh);

  return unrotated;
}

/**
 * Dispatch to the per-algorithm cell-mask producer. Output mask
 * is 1 where a dot should be painted, 0 elsewhere. Polarity is
 * applied here so downstream rendering doesn't need to know.
 */
function computeCellMask(cellGray: ImageData, method: FamilyCMethod): Uint8Array {
  switch (method.algorithm) {
    case 'bayer':
      return orderedCellMask(cellGray, method.density, method.polarity, (x, y) => {
        const m = BAYER_MATRICES[method.matrixSize];
        const ms = method.matrixSize;
        const ma = ms * ms;
        const ix = ((x % ms) + ms) % ms;
        const iy = ((y % ms) + ms) % ms;
        return ((m[iy * ms + ix] ?? 0) + 0.5) / ma;
      });
    case 'bluenoise':
      return orderedCellMask(cellGray, method.density, method.polarity, (x, y) => {
        const tx = ((x % BLUE_NOISE_SIZE) + BLUE_NOISE_SIZE) % BLUE_NOISE_SIZE;
        const ty = ((y % BLUE_NOISE_SIZE) + BLUE_NOISE_SIZE) % BLUE_NOISE_SIZE;
        return ((BLUE_NOISE_128[ty * BLUE_NOISE_SIZE + tx] ?? 0) + 0.5) / 256;
      });
    case 'floyd-steinberg':
      return errorDiffMask(floydSteinbergMask(cellGray, method), method.polarity);
    case 'atkinson':
      return errorDiffMask(atkinsonMask(cellGray, method), method.polarity);
    case 'werness':
      return errorDiffMask(wernessMask(cellGray, method), method.polarity);
  }
}

/**
 * Per-cell ordered threshold compare. `thresholdAt(x, y)` returns
 * a value in [0, 1] from the algorithm's threshold tile. The cell
 * is "on" (= 1) when the source is dark enough for that
 * threshold; polarity flips which end of the source range turns
 * dots on.
 */
function orderedCellMask(
  cellGray: ImageData,
  density: number,
  polarity: Polarity,
  thresholdAt: (x: number, y: number) => number,
): Uint8Array {
  const w = cellGray.width;
  const h = cellGray.height;
  const data = cellGray.data;
  const densityShift = (50 - density) / 50;
  const polarityWhite = polarity === 'white';
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      // SATURATION_FLOOR keeps the post-shift gray above 0 so the
      // compare against the algorithm's threshold tile (0..1) still
      // resolves both ways across the image. At density 100 every
      // cell would otherwise unconditionally turn on, the stamp
      // silhouette in the upscale pass would fill every cell, and
      // the output would read as a solid block. See halftone.ts
      // for the matching clamp.
      const grayShift = (data[i * 4] ?? 0) / 255 + densityShift;
      const gray = grayShift < SATURATION_FLOOR ? SATURATION_FLOOR : grayShift;
      const threshold = thresholdAt(x, y);
      const compareLeft = polarityWhite ? 1 - gray : gray;
      out[i] = compareLeft <= threshold ? 1 : 0;
    }
  }
  return out;
}

/** See halftone.ts - same value, same purpose. */
const SATURATION_FLOOR = 0.05;

/**
 * Translate the existing error-diffusion mask convention into
 * Family C's "1 = paint" convention. The legacy mask uses 1 for
 * "bright / skipped" and 0 for "dark / painted"; Family C wants
 * the inverse so renderCellMaskWithStamp doesn't have to know
 * which algorithm produced the mask.
 */
function errorDiffMask(mask: Uint8Array, polarity: Polarity): Uint8Array {
  const out = new Uint8Array(mask.length);
  if (polarity === 'white') {
    // Noir: bright source painted. Legacy mask=1 (bright) → on.
    for (let i = 0; i < mask.length; i++) out[i] = mask[i] ?? 0;
  } else {
    // Classic: dark source painted. Legacy mask=0 (dark) → on.
    for (let i = 0; i < mask.length; i++) out[i] = mask[i] === 0 ? 1 : 0;
  }
  return out;
}

/**
 * Paint the mask at the cell-grid resolution × cellSize. For
 * each "on" cell the stamp threshold function defines which
 * pixels fall inside the dot's silhouette (threshold > 0). Off
 * cells are rendered as the background colour.
 *
 * Output dimensions = (cellW · cellSize, cellH · cellSize). May
 * differ from the rotated source by up to (cellSize - 1) pixels
 * on each axis because downsampleBoxAverage floors the source
 * dims; the stage-5 rotate-with-outDims call snaps everything
 * back to the original source size.
 */
function renderCellMaskWithStamp(
  cellMask: Uint8Array,
  cellW: number,
  cellH: number,
  cellSize: number,
  stamp: FamilyCMethod['stamp'],
  shapeAngleDeg: number,
  shapeFlipX: boolean,
  shapeFlipY: boolean,
  polarity: Polarity,
  boundaryNoiseStrength: number,
  boundaryNoiseScale: number,
): ImageData {
  const w = cellW * cellSize;
  const h = cellH * cellSize;
  const out = new ImageData(w, h);
  const dst = out.data;
  const stampTh = buildStampThreshold(stamp, shapeAngleDeg, {
    flipX: shapeFlipX,
    flipY: shapeFlipY,
  });
  const fgValue = polarity === 'black' ? 0 : 255;
  const bgValue = polarity === 'black' ? 255 : 0;
  const edgeJitterRadius = boundaryJitterRadiusUv(
    boundaryNoiseStrength,
    cellSize,
  );
  const noiseScale = Math.min(16, Math.max(1, boundaryNoiseScale));

  for (let py = 0; py < h; py++) {
    const cy = Math.floor(py / cellSize);
    for (let px = 0; px < w; px++) {
      const cx = Math.floor(px / cellSize);
      const di = (py * w + px) * 4;
      const isOn = cellMask[cy * cellW + cx] === 1;
      let v: number;
      if (!isOn) {
        v = bgValue;
      } else {
        let u = (px - cx * cellSize + 0.5) / cellSize;
        let uv = (py - cy * cellSize + 0.5) / cellSize;
        if (edgeJitterRadius > 0) {
          const nx = sampleBlueNoiseBilinear(px, py, noiseScale) / 255 - 0.5;
          const ny =
            sampleBlueNoiseBilinear(px + 73.17, py + 19.31, noiseScale) / 255 - 0.5;
          u += nx * 2 * edgeJitterRadius;
          uv += ny * 2 * edgeJitterRadius;
        }
        // threshold > 0 = inside the dot's footprint. The exact
        // 0 cutoff matches the buildStampThreshold semantics:
        // every shape returns 0 outside its coverage extent.
        v = stampTh(u, uv) > 0 ? fgValue : bgValue;
      }
      dst[di] = v;
      dst[di + 1] = v;
      dst[di + 2] = v;
      dst[di + 3] = 255;
    }
  }

  return out;
}

function boundaryJitterRadiusUv(strength: number, cellSize: number): number {
  const clampedStrength = Math.min(1, Math.max(0, strength));
  if (clampedStrength <= 0) return 0;
  const maxPixels = Math.min(2, cellSize * 0.24);
  return (maxPixels * clampedStrength) / cellSize;
}

/**
 * Mutate `target` in place: any pixel whose ORIGINAL source
 * value falls below blackTh becomes solid black; any pixel above
 * whiteTh becomes solid white. Mid-tone pixels are left alone so
 * the dot pattern from earlier stages remains visible.
 *
 * `target` and `original` must share dimensions - that's
 * guaranteed because stage 5 already cropped to the source's
 * (W, H).
 */
function applySolidInputClamp(
  target: ImageData,
  original: ImageData,
  polarity: Polarity,
  blackTh: number,
  whiteTh: number,
): void {
  const w = target.width;
  const h = target.height;
  if (original.width !== w || original.height !== h) {
    return;
  }
  const dst = target.data;
  const src = original.data;
  // Solid black always renders pure black, solid white pure
  // white - these two are polarity-invariant so users get
  // consistent line-art behaviour whether they're on classic or
  // noir mode. Polarity only affects the dithered mid-range.
  void polarity;
  for (let i = 0; i < src.length; i += 4) {
    const gray = (src[i] ?? 0) / 255;
    if (gray <= blackTh) {
      dst[i] = 0;
      dst[i + 1] = 0;
      dst[i + 2] = 0;
      dst[i + 3] = 255;
    } else if (gray >= whiteTh) {
      dst[i] = 255;
      dst[i + 1] = 255;
      dst[i + 2] = 255;
      dst[i + 3] = 255;
    }
  }
}
